import React from 'react';
import { Home, Pill, User, Menu, Bell, Phone, MessageCircle, TrendingUp, Plus, Download, ArrowLeft, AlertCircle, Clock, CheckCircle, AlertTriangle, Edit, RefreshCw, Search, Shield, Users, LogOut, HelpCircle, ChevronRight, Volume2, X } from 'lucide-react';
import { medicationService, medicationDoseService } from '../services/supabaseService';
import { convertDosesToSupportProgress, formatTimeToReadable } from '../utils/doseTimeUtils';

/**
 * Support App Prototype
 * Mobile-first monitoring interface
 * Props: screen, onNavigate
 */

export const SupportLanding = ({ onNavigate }) => {
  const features = [
    {icon: Bell, title: 'Real-Time Alerts', description: 'Get notified about missed medications', color: 'blue'},
    {icon: TrendingUp, title: 'Health Insights', description: 'Track patterns and trends', color: 'green'},
    {icon: MessageCircle, title: 'Stay Connected', description: 'Send messages and coordinate care', color: 'purple'}
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
            <User className="w-10 h-10 text-white"/>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Family Care Dashboard</h1>
          <p className="text-lg text-gray-600">Stay connected with your loved one's health</p>
        </div>
        <div className="space-y-4 mb-8">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <div key={index} className={`flex items-start p-4 bg-${feature.color}-50 rounded-xl border border-${feature.color}-100`}>
                <div className={`w-10 h-10 bg-${feature.color}-500 rounded-lg flex items-center justify-center mr-4`}>
                  <FeatureIcon className="w-5 h-5 text-white"/>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{feature.title}</p>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={() => onNavigate('home')} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-lg font-semibold py-4 rounded-xl shadow-lg">
          Access Dashboard
        </button>
      </div>
    </div>
  );
};

export const SupportHome = ({ onNavigate, medications = [] }) => {
  // Get progress items from first medication's doses
  const todayProgress = React.useMemo(() => {
    if (!medications || medications.length === 0) return [];
    const med = medications[0];
    if (!med || !med.doses || med.doses.length === 0) return [];
    
    return convertDosesToSupportProgress(med.doses);
  }, [medications]);

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Menu className="w-6 h-6 text-gray-600 mr-4"/>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mom's Health</h1>
              <p className="text-sm text-gray-600">Margaret Thompson</p>
            </div>
          </div>
          <div className="relative">
            <Bell className="w-6 h-6 text-gray-600"/>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">2</span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm font-medium">Active now</span>
          </div>
          <span className="text-sm">Last medication: 23 min ago</span>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-orange-50 px-4 py-3 border-b border-orange-100 flex items-center">
            <AlertCircle className="w-5 h-5 text-orange-600 mr-2"/>
            <h2 className="font-bold text-gray-900">ACTIVE ALERTS (2)</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="border-2 border-yellow-300 bg-yellow-50 rounded-lg p-4">
              <div className="flex justify-between mb-3">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <AlertTriangle className="w-5 h-5 text-white"/>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Evening dose delayed</p>
                    {medications && medications.length > 0 && medications[0] && medications[0].doses && medications[0].doses.length > 0 ? (
                      <p className="text-sm text-gray-600 mt-1">
                        {medications[0].name || 'Medication'} - Due {formatTimeToReadable(medications[0].doses[0].scheduled_time)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600 mt-1">Medication - Due 7:00 PM</p>
                    )}
                    <p className="text-sm text-gray-600">Snoozed twice, 45 min late</p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">3 min ago</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onNavigate('alert-detail')} className="flex-1 bg-white border border-gray-300 text-gray-700 text-sm font-medium py-2 rounded-lg">VIEW</button>
                <button className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center">
                  <Phone className="w-4 h-4 mr-1"/>CALL
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <TrendingUp className="w-6 h-6 text-blue-600 mr-2"/>
              <h2 className="text-lg font-bold text-gray-900">TODAY'S ADHERENCE</h2>
            </div>
            <span className="text-2xl font-bold text-blue-600">80%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full" style={{width: '80%'}}></div>
          </div>
          <div className="space-y-3">
            {todayProgress.map((item, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${item.completed ? 'bg-green-50' : 'bg-orange-50'}`}>
                <div className="flex items-center">
                  {item.completed ? <CheckCircle className="w-5 h-5 text-green-600 mr-3"/> : <Clock className="w-5 h-5 text-orange-600 mr-3"/>}
                  <div>
                    <p className="font-medium text-gray-900">{item.time}</p>
                    <p className="text-sm text-gray-600">{item.status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button className="bg-white border-2 border-blue-600 text-blue-600 font-semibold py-4 rounded-xl flex items-center justify-center">
            <Phone className="w-5 h-5 mr-2"/>Call Mom
          </button>
          <button className="bg-white border-2 border-blue-600 text-blue-600 font-semibold py-4 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-5 h-5 mr-2"/>Message
          </button>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg max-w-md mx-auto">
        <div className="flex justify-around p-3">
          <button onClick={() => onNavigate('home')} className="flex flex-col items-center text-blue-600">
            <Home className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Home</span>
          </button>
          <button onClick={() => onNavigate('trends')} className="flex flex-col items-center text-gray-400">
            <TrendingUp className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Trends</span>
          </button>
          <button onClick={() => onNavigate('meds')} className="flex flex-col items-center text-gray-400">
            <Pill className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Meds</span>
          </button>
          <button onClick={() => onNavigate('more')} className="flex flex-col items-center text-gray-400">
            <User className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const SupportAlertDetail = ({ onNavigate, medications = [] }) => {
  // Calculate timeline based on medication's first dose time
  const timeline = React.useMemo(() => {
    const baseTime = medications && medications.length > 0 && medications[0] && medications[0].doses && medications[0].doses.length > 0
      ? medications[0].doses[0].scheduled_time
      : '19:00'; // Default to 7:00 PM if no medication data
    
    const [hour, minute] = baseTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    
    const formatTime = (hours, minutes) => {
      const d = new Date(date);
      d.setHours(hours, minutes, 0, 0);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };
    
    return [
      {time: formatTime(hour, minute), event: 'Initial reminder sent'},
      {time: formatTime(hour, minute + 15), event: 'Mom snoozed (15 min)'},
      {time: formatTime(hour, minute + 30), event: 'Second reminder'},
      {time: formatTime(hour, minute + 35), event: 'Mom snoozed (10 min)'},
      {time: formatTime(hour, minute + 45), event: 'Third reminder (now)'}
    ];
  }, [medications]);

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => onNavigate('home')} className="mr-4 p-2">
              <ArrowLeft className="w-6 h-6"/>
            </button>
            <h1 className="text-xl font-bold">Alert Details</h1>
          </div>
          <span className="text-xs text-gray-500">3 minutes ago</span>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4">
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4">
              <AlertTriangle className="w-6 h-6 text-white"/>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">MEDICATION DELAYED</h2>
              <p className="text-base text-gray-700 mb-1">Mom has delayed her evening medication by 45 minutes</p>
              <p className="text-sm text-gray-600">Maximum snoozes reached. Alert escalated to you.</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4">TIMELINE</h3>
          <div className="space-y-4">
            {timeline.map((item, idx) => (
              <div key={idx} className="flex items-start">
                <div className="flex flex-col items-center mr-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 4 ? 'bg-orange-500' : 'bg-blue-500'}`}>
                    <Clock className="w-5 h-5 text-white"/>
                  </div>
                  {idx < 4 && <div className="w-0.5 h-8 bg-gray-300 my-1"></div>}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{item.time}</p>
                  <p className="text-sm text-gray-600">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4">SUGGESTED ACTIONS</h3>
          <div className="space-y-3">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 mr-2"/>CALL MOM NOW
            </button>
            <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 mr-2"/>SEND GENTLE REMINDER
            </button>
            <button onClick={() => onNavigate('home')} className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 mr-2"/>MARK AS RESOLVED
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-5">
          <div className="flex items-start mb-4">
            <TrendingUp className="w-6 h-6 text-indigo-600 mr-3"/>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">PATTERN DETECTED</h3>
              <p className="text-sm text-gray-700 mb-3">Evening doses consistently delayed - Usually during dinner prep</p>
            </div>
          </div>
          <div className="bg-yellow-100 border-l-4 border-yellow-500 rounded p-4">
            <p className="text-sm font-semibold text-gray-900 mb-1">💡 SUGGESTION:</p>
            <p className="text-sm text-gray-700 mb-3">Consider moving evening dose from 7:00 PM to 6:30 PM</p>
            <button className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-semibold py-2 px-4 rounded-lg">
              ADJUST SCHEDULE
            </button>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg max-w-md mx-auto">
        <div className="flex justify-around p-3">
          <button onClick={() => onNavigate('home')} className="flex flex-col items-center text-blue-600">
            <Home className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Home</span>
          </button>
          <button onClick={() => onNavigate('trends')} className="flex flex-col items-center text-gray-400">
            <TrendingUp className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Trends</span>
          </button>
          <button onClick={() => onNavigate('meds')} className="flex flex-col items-center text-gray-400">
            <Pill className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Meds</span>
          </button>
          <button onClick={() => onNavigate('more')} className="flex flex-col items-center text-gray-400">
            <User className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const SupportTrends = ({ onNavigate }) => {
  const medicationCriticality = [
    {name: 'Lisinopril 10mg', purpose: 'Blood pressure', rate: 100, criticality: 'critical', reason: 'Skipping can cause dangerous spikes'},
    {name: 'Vitamin D', purpose: 'Bone health', rate: 79, criticality: 'low', reason: 'Effects are gradual'}
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => onNavigate('home')} className="mr-4 p-2">
              <ArrowLeft className="w-6 h-6"/>
            </button>
            <h1 className="text-xl font-bold">Health Trends</h1>
          </div>
          <button className="text-blue-600 text-sm font-medium flex items-center">
            <Download className="w-4 h-4 mr-1"/>Export
          </button>
        </div>
      </div>
      <div className="px-4 py-6 space-y-4">
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm opacity-90">Overall Adherence</p>
              <h2 className="text-4xl font-bold">94%</h2>
            </div>
            <div className="text-right">
              <div className="flex items-center text-green-300">
                <TrendingUp className="w-5 h-5 mr-1"/>
                <span className="font-semibold">+3%</span>
              </div>
              <p className="text-sm opacity-75">vs last week</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-5">
          <h3 className="font-bold mb-4">Medication Criticality</h3>
          <div className="space-y-3">
            {medicationCriticality.map((med, index) => (
              <div key={index} className={`border-2 rounded-lg p-4 ${med.criticality === 'critical' ? 'border-red-600 bg-red-50' : 'border-blue-500 bg-blue-50'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold">{med.name}</p>
                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${med.criticality === 'critical' ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-blue-100 text-blue-700 border border-blue-300'}`}>
                      {med.criticality.toUpperCase()}
                    </span>
                    <p className="text-xs mt-2 text-gray-700"><strong>If missed:</strong> {med.reason}</p>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{med.rate}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg max-w-md mx-auto">
        <div className="flex justify-around p-3">
          <button onClick={() => onNavigate('home')} className="flex flex-col items-center text-gray-400">
            <Home className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Home</span>
          </button>
          <button onClick={() => onNavigate('trends')} className="flex flex-col items-center text-blue-600">
            <TrendingUp className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Trends</span>
          </button>
          <button onClick={() => onNavigate('meds')} className="flex flex-col items-center text-gray-400">
            <Pill className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Meds</span>
          </button>
          <button onClick={() => onNavigate('more')} className="flex flex-col items-center text-gray-400">
            <User className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const SupportMeds = ({ onNavigate }) => {
  const [medsData, setMedsData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editingMed, setEditingMed] = React.useState(null);
  const [formData, setFormData] = React.useState({
    name: '',
    dosage: '',
    time: '',
    schedule: '',
    doctor: '',
    purpose: '',
    form: '',
    location: '',
    refills: 0,
    next_refill_date: '',
    status: 'active',
    doseTimes: {} // e.g., { dose_1: '09:00', dose_2: '17:00', dose_3: '01:00' }
  });
  const [submitting, setSubmitting] = React.useState(false);

  // Helper function to calculate default dose times based on schedule and time
  const calculateDefaultDoseTimes = (schedule, time) => {
    if (!schedule || !time) return {};
    
    const [firstHour, firstMinute] = time.split(':').map(Number);
    if (isNaN(firstHour) || isNaN(firstMinute)) return {};
    
    const doseTimes = {};
    
    if (schedule === 'once a day') {
      doseTimes.dose_1 = `${String(firstHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
    } else if (schedule === 'twice a day') {
      doseTimes.dose_1 = `${String(firstHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
      const secondHour = (firstHour + 8) % 24;
      doseTimes.dose_2 = `${String(secondHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
    } else if (schedule === '3 times a day') {
      doseTimes.dose_1 = `${String(firstHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
      const secondHour = (firstHour + 8) % 24;
      doseTimes.dose_2 = `${String(secondHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
      const thirdHour = (firstHour + 16) % 24;
      doseTimes.dose_3 = `${String(thirdHour).padStart(2, '0')}:${String(firstMinute).padStart(2, '0')}`;
    }
    
    return doseTimes;
  };

  // Auto-calculate dose times when schedule and time are both set (only if doseTimes is empty)
  React.useEffect(() => {
    if (formData.schedule && formData.time && (!formData.doseTimes || Object.keys(formData.doseTimes).length === 0)) {
      const defaultDoseTimes = calculateDefaultDoseTimes(formData.schedule, formData.time);
      if (Object.keys(defaultDoseTimes).length > 0) {
        setFormData(prev => {
          // Only update if doseTimes is still empty (avoid race conditions)
          if (!prev.doseTimes || Object.keys(prev.doseTimes).length === 0) {
            return { ...prev, doseTimes: defaultDoseTimes };
          }
          return prev;
        });
      }
    }
  }, [formData.schedule, formData.time, formData.doseTimes]);

  const fetchMedications = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await medicationService.getAll();
      // Transform Supabase data to match component format
      const transformedData = data.map(med => ({
        id: med.id,
        name: med.name,
        dose: med.dosage,
        sched: med.schedule || med.time,
        doc: med.doctor || 'N/A',
        adh: med.adherence || 100,
        status: med.status || 'active',
        refills: med.refills || 0,
        next: med.next_refill_date || 'N/A',
        days: med.days_until_refill,
        raw: med // Store full raw data for editing
      }));
      setMedsData(transformedData);
    } catch (error) {
      console.error('Failed to fetch medications:', error);
      setMedsData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const openAddModal = () => {
    setEditingMed(null);
    setFormData({
      name: '',
      dosage: '',
      time: '',
      schedule: '',
      doctor: '',
      purpose: '',
      form: '',
      location: '',
      refills: 0,
      next_refill_date: '',
      status: 'active',
      doseTimes: {}
    });
    setShowModal(true);
  };

  const openEditModal = async (med) => {
    const rawMed = med.raw || med;
    setEditingMed(rawMed.id);
    
    // Format date for input field (YYYY-MM-DD)
    let formattedDate = '';
    if (rawMed.next_refill_date) {
      const date = new Date(rawMed.next_refill_date);
      if (!isNaN(date.getTime())) {
        formattedDate = date.toISOString().split('T')[0];
      }
    }
    
    // Load existing dose times
    let doseTimes = {};
    try {
      const existingDoses = await medicationDoseService.getByMedicationId(rawMed.id);
      existingDoses.forEach(dose => {
        // Convert TIME to HH:MM format for time input
        const timeStr = dose.scheduled_time;
        doseTimes[`dose_${dose.dose_number}`] = timeStr.length === 5 ? timeStr : timeStr.substring(0, 5);
      });
    } catch (error) {
      console.error('Error loading dose times:', error);
    }
    
    setFormData({
      name: rawMed.name || '',
      dosage: rawMed.dosage || '',
      time: rawMed.time || '',
      schedule: rawMed.schedule || rawMed.time || '',
      doctor: rawMed.doctor || '',
      purpose: rawMed.purpose || '',
      form: rawMed.form || '',
      location: rawMed.location || '',
      refills: rawMed.refills || 0,
      next_refill_date: formattedDate,
      status: rawMed.status || 'active',
      doseTimes: doseTimes
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMed(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const medicationData = {
        name: formData.name,
        dosage: formData.dosage,
        time: formData.time,
        schedule: formData.schedule || formData.time,
        doctor: formData.doctor || null,
        purpose: formData.purpose || null,
        form: formData.form || null,
        location: formData.location || null,
        refills: formData.refills || 0,
        next_refill_date: formData.next_refill_date || null,
        status: formData.status || 'active',
        adherence: 100 // Default for new medications
      };

      let medicationId;
      if (editingMed) {
        const updated = await medicationService.update(editingMed, medicationData);
        medicationId = updated.id;
      } else {
        const created = await medicationService.create(medicationData);
        medicationId = created.id;
      }

      // Save dose times - use formData.doseTimes directly (already calculated/edited in UI)
      if (medicationId && formData.schedule && formData.doseTimes) {
        const doses = Object.keys(formData.doseTimes)
          .sort() // Sort to ensure dose_1, dose_2, dose_3 order
          .map(key => formData.doseTimes[key]);
        
        if (doses.length > 0) {
          await medicationDoseService.upsertDoses(medicationId, doses);
        }
      }

      closeModal();
      await fetchMedications();
    } catch (error) {
      console.error('Failed to save medication:', error);
      alert('Failed to save medication. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // Recalculate dose times when time or schedule changes (only if doseTimes haven't been manually edited)
      if ((name === 'time' || name === 'schedule') && value && updated.schedule && updated.time) {
        const defaultDoseTimes = calculateDefaultDoseTimes(updated.schedule, updated.time);
        const newDoseTimes = { ...prev.doseTimes };
        
        // Only update if the calculated value matches what's currently there (meaning it wasn't manually edited)
        Object.keys(defaultDoseTimes).forEach(key => {
          if (!newDoseTimes[key] || newDoseTimes[key] === defaultDoseTimes[key]) {
            newDoseTimes[key] = defaultDoseTimes[key];
          }
        });
        
        // Remove doses that are no longer needed based on schedule
        if (updated.schedule === 'once a day') {
          delete newDoseTimes.dose_2;
          delete newDoseTimes.dose_3;
        } else if (updated.schedule === 'twice a day') {
          delete newDoseTimes.dose_3;
        }
        
        updated.doseTimes = newDoseTimes;
      }
      
      return updated;
    });
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <button onClick={() => onNavigate('home')} className="mr-4 p-2">
              <ArrowLeft className="w-6 h-6"/>
            </button>
            <div>
              <h1 className="text-xl font-bold">Manage Medications</h1>
              <p className="text-sm text-gray-600">
                {loading ? 'Loading...' : `${medsData.length} active medications`}
              </p>
            </div>
          </div>
          <button onClick={openAddModal} className="bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-lg flex items-center">
            <Plus className="w-4 h-4 mr-1"/>Add
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"/>
          <input type="text" placeholder="Search medications" className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg"/>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading medications...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-6 h-6 text-green-600 mb-1"/>
                <p className="text-xs text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {medsData.filter(m => m.status === 'active').length}
                </p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <AlertTriangle className="w-6 h-6 text-orange-600 mb-1"/>
                <p className="text-xs text-gray-600">Refill</p>
                <p className="text-2xl font-bold text-orange-600">
                  {medsData.filter(m => m.status === 'refill').length}
                </p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mb-1"/>
                <p className="text-xs text-gray-600">Low Adh.</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {medsData.filter(m => m.status === 'low').length}
                </p>
              </div>
            </div>

            {medsData.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No medications found</p>
              </div>
            ) : (
              medsData.map((med) => (
          <div key={med.id || med.name} className={`bg-white rounded-xl shadow-md p-5 border-2 ${med.status === 'refill' ? 'border-orange-300' : med.status === 'low' ? 'border-yellow-300' : 'border-gray-200'}`}>
            {med.status !== 'active' && (
              <div className={`mb-4 px-3 py-2 rounded-lg flex items-center ${med.status === 'refill' ? 'bg-orange-100' : 'bg-yellow-100'}`}>
                <AlertTriangle className={`w-4 h-4 mr-2 ${med.status === 'refill' ? 'text-orange-600' : 'text-yellow-600'}`}/>
                <span className={`text-sm font-medium ${med.status === 'refill' ? 'text-orange-800' : 'text-yellow-800'}`}>
                  {med.status === 'refill' ? `Refill needed in ${med.days} days` : `Low adherence (${med.adh}%)`}
                </span>
              </div>
            )}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start flex-1">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <Pill className="w-6 h-6 text-blue-600"/>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{med.name} {med.dose}</h3>
                  <p className="text-sm text-gray-600 mt-1">Schedule: {med.sched}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center text-gray-600 mb-1">
                  <Clock className="w-4 h-4 mr-2"/>
                  <span className="text-xs font-medium">SCHEDULE</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{med.sched}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center text-gray-600 mb-1">
                  <User className="w-4 h-4 mr-2"/>
                  <span className="text-xs font-medium">DOCTOR</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{med.doc}</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Adherence:</span>
                <span className={`font-bold ${med.adh >= 95 ? 'text-green-600' : med.adh >= 85 ? 'text-blue-600' : 'text-orange-600'}`}>{med.adh}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${med.adh >= 95 ? 'bg-green-500' : med.adh >= 85 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{width: `${med.adh}%`}}></div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEditModal(med)} className="flex-1 bg-gray-100 text-gray-800 text-sm font-medium py-2 rounded-lg flex items-center justify-center">
                <Edit className="w-4 h-4 mr-2"/>Edit
              </button>
              {med.status === 'refill' && (
                <button className="flex-1 bg-orange-600 text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-4 h-4 mr-2"/>Refill
                </button>
              )}
            </div>
          </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Medication Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingMed ? 'Edit Medication' : 'Add New Medication'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-600"/>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medication Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Lisinopril"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dosage <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 10mg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Dose Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule
                </label>
                <select
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select schedule</option>
                  <option value="once a day">once a day</option>
                  <option value="twice a day">twice a day</option>
                  <option value="3 times a day">3 times a day</option>
                </select>
              </div>

              {/* Review Planned Reminders Section */}
              {formData.schedule && formData.time && (() => {
                // Ensure doseTimes are calculated if not present
                const doseTimes = formData.doseTimes && Object.keys(formData.doseTimes).length > 0 
                  ? formData.doseTimes 
                  : calculateDefaultDoseTimes(formData.schedule, formData.time);
                
                if (Object.keys(doseTimes).length === 0) return null;
                
                return (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Review your planned reminders
                    </label>
                    <div className="space-y-3">
                      {Object.entries(doseTimes)
                        .sort(([a], [b]) => {
                          // Sort by dose number (dose_1, dose_2, dose_3)
                          const numA = parseInt(a.replace('dose_', ''));
                          const numB = parseInt(b.replace('dose_', ''));
                          return numA - numB;
                        })
                        .map(([key, time]) => {
                          const doseNumber = parseInt(key.replace('dose_', ''));
                          return (
                            <div key={key} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-sm font-semibold text-blue-600">{doseNumber}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Dose {doseNumber}</p>
                                  <p className="text-xs text-gray-500">Reminder time</p>
                                </div>
                              </div>
                              <input
                                type="time"
                                value={time}
                                onChange={(e) => {
                                  const currentDoseTimes = formData.doseTimes && Object.keys(formData.doseTimes).length > 0 
                                    ? formData.doseTimes 
                                    : doseTimes;
                                  const newDoseTimes = { ...currentDoseTimes, [key]: e.target.value };
                                  setFormData({ ...formData, doseTimes: newDoseTimes });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                          );
                        })}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Times are calculated based on your first dose time and schedule. You can adjust them individually.
                    </p>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor
                </label>
                <input
                  type="text"
                  name="doctor"
                  value={formData.doctor}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Dr. Patel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <input
                  type="text"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Blood pressure"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form
                </label>
                <input
                  type="text"
                  name="form"
                  value={formData.form}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Small white round tablet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., White bottle with blue cap"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refills
                  </label>
                  <input
                    type="number"
                    name="refills"
                    value={formData.refills}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Refill Date
                  </label>
                  <input
                    type="date"
                    name="next_refill_date"
                    value={formData.next_refill_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="refill">Refill Needed</option>
                  <option value="low">Low Adherence</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 text-gray-800 font-medium py-3 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-blue-600 text-white font-medium py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : editingMed ? 'Update' : 'Add Medication'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg max-w-md mx-auto">
        <div className="flex justify-around p-3">
          <button onClick={() => onNavigate('home')} className="flex flex-col items-center text-gray-400">
            <Home className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Home</span>
          </button>
          <button onClick={() => onNavigate('trends')} className="flex flex-col items-center text-gray-400">
            <TrendingUp className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Trends</span>
          </button>
          <button onClick={() => onNavigate('meds')} className="flex flex-col items-center text-blue-600">
            <Pill className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Meds</span>
          </button>
          <button onClick={() => onNavigate('more')} className="flex flex-col items-center text-gray-400">
            <User className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const SupportMore = ({ onNavigate }) => {
  const settingsMenus = [
    {
      title: 'App Settings',
      items: [
        {icon: Bell, label: 'Notifications', sub: 'Alerts, quiet hours', color: 'blue'},
        {icon: AlertTriangle, label: 'Escalation Rules', sub: 'When to alert you', color: 'orange'},
        {icon: Volume2, label: 'Assistant Behavior', sub: 'Voice, reminders', color: 'purple'},
        {icon: Shield, label: 'Privacy & Permissions', sub: 'Data access', color: 'green'}
      ]
    },
    {
      title: 'Care Team',
      items: [
        {icon: Users, label: 'Manage Care Team', sub: '1 support person, 2 providers', color: 'indigo'},
        {icon: Phone, label: 'Emergency Contacts', sub: '3 contacts configured', color: 'red'}
      ]
    },
    {
      title: 'Data & Reports',
      items: [
        {icon: Download, label: 'Export Health Data', sub: 'Download reports', color: 'blue'},
        {icon: MessageCircle, label: 'Conversation History', sub: 'View past interactions', color: 'gray'}
      ]
    },
    {
      title: 'Support',
      items: [
        {icon: HelpCircle, label: 'Help Center', sub: 'FAQs and guides', color: 'yellow'},
        {icon: MessageCircle, label: 'Contact Support', sub: '24/7 customer service', color: 'green'}
      ]
    }
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm p-4">
        <div className="flex items-center">
          <button onClick={() => onNavigate('home')} className="mr-4 p-2">
            <ArrowLeft className="w-6 h-6"/>
          </button>
          <h1 className="text-xl font-bold">More</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4">
        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-4">
              <User className="w-8 h-8 text-white"/>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">Sarah Johnson</h2>
              <p className="text-sm text-gray-600">Daughter • Primary Support</p>
              <p className="text-sm text-blue-600 mt-1">sarah.j@email.com</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400"/>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase">Care Recipients</h3>
            <button className="text-blue-600 text-sm font-medium flex items-center">
              <Plus className="w-4 h-4 mr-1"/>Add
            </button>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mr-4">
                  <User className="w-6 h-6 text-blue-700"/>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Margaret Thompson</p>
                  <p className="text-sm text-gray-600">Mother • Age 78</p>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full mt-1 inline-block">Active</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600"/>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-4 border-t border-blue-200">
              <button className="bg-white text-blue-700 text-sm font-medium py-2 rounded-lg flex items-center justify-center">
                <Phone className="w-4 h-4 mr-1"/>Call
              </button>
              <button className="bg-white text-blue-700 text-sm font-medium py-2 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 mr-1"/>Message
              </button>
            </div>
          </div>
        </div>

        {settingsMenus.map((menu, menuIndex) => (
          <div key={menuIndex} className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">{menu.title}</h3>
            </div>
            {menu.items.map((item, itemIndex) => {
              const ItemIcon = item.icon;
              return (
                <button key={itemIndex} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 border-b last:border-0">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 bg-${item.color}-100 rounded-lg flex items-center justify-center mr-3`}>
                      <ItemIcon className={`w-5 h-5 text-${item.color}-600`}/>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.sub}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400"/>
                </button>
              );
            })}
          </div>
        ))}

        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="font-medium text-gray-900 mb-1">Virtual Health Assistant</p>
          <p className="text-sm text-gray-500">Version 1.0.0</p>
          <p className="text-xs text-gray-500 mt-2">© 2025 VHA Technologies</p>
        </div>

        <button className="w-full bg-white border-2 border-red-200 text-red-600 font-semibold py-3 rounded-xl flex items-center justify-center">
          <LogOut className="w-5 h-5 mr-2"/>Log Out
        </button>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg max-w-md mx-auto">
        <div className="flex justify-around p-3">
          <button onClick={() => onNavigate('home')} className="flex flex-col items-center text-gray-400">
            <Home className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Home</span>
          </button>
          <button onClick={() => onNavigate('trends')} className="flex flex-col items-center text-gray-400">
            <TrendingUp className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Trends</span>
          </button>
          <button onClick={() => onNavigate('meds')} className="flex flex-col items-center text-gray-400">
            <Pill className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">Meds</span>
          </button>
          <button onClick={() => onNavigate('more')} className="flex flex-col items-center text-blue-600">
            <User className="w-6 h-6 mb-1"/><span className="text-xs font-semibold">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

